/**
 * Estados de rota (SPEC 4.0, 10.7): erro isolado por tela, carregando (lazy) e a página em
 * construção. "Sem acesso" e 404 ficam em `RouteNotice.tsx`, que só desce quando aparecem (10.4).
 */
import { Construction, TriangleAlert } from 'lucide-react';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '@/components/Button';
import { Spinner } from '@/components/Spinner';
import styles from './App.module.css';
import { appCopy, ROUTE_PURPOSE, ROUTE_TITLES } from './copy';
import type { RouteId } from './permissions';

/** Fallback do `<Suspense>` de cada rota lazy. */
export function RouteLoading() {
  return (
    <div className={styles.loading}>
      <Spinner label={appCopy.routeLoading} />
    </div>
  );
}

interface BoundaryProps {
  /** Muda a cada rota: o erro de uma tela não persiste na próxima. */
  resetKey: string;
  children: ReactNode;
}
interface BoundaryState {
  error: Error | null;
  key: string;
}

/** Error boundary por rota: uma tela quebrada não derruba o shell (4.0). */
export class RouteErrorBoundary extends Component<BoundaryProps, BoundaryState> {
  override state: BoundaryState = { error: null, key: this.props.resetKey };

  static getDerivedStateFromError(error: Error): Partial<BoundaryState> {
    return { error };
  }

  static getDerivedStateFromProps(props: BoundaryProps, state: BoundaryState): Partial<BoundaryState> | null {
    return props.resetKey !== state.key ? { error: null, key: props.resetKey } : null;
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[Rota] erro na tela', error, info.componentStack);
  }

  private retry = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <section className={styles.state} role="alert">
        <span className={styles.stateIcon} aria-hidden="true">
          <TriangleAlert size={28} strokeWidth={1.75} />
        </span>
        <h2 className={styles.stateTitle}>{appCopy.routeError.title}</h2>
        <p className={styles.stateText}>{appCopy.routeError.text}</p>
        <Button variant="secondary" onPress={this.retry}>
          {appCopy.routeError.retry}
        </Button>
      </section>
    );
  }
}

/**
 * Página em construção (onda 2 substitui). O título grande repete o da toolbar como
 * `h2`: o `h1` da página é o título da toolbar (10.7).
 */
export function PagePlaceholder({ route }: { route: RouteId }) {
  return (
    <div className={styles.placeholder} data-testid={`pagina-${route}`}>
      <section className={styles.hero} aria-labelledby={`placeholder-${route}`}>
        <span className={styles.badge}>
          <Construction size={14} strokeWidth={1.75} aria-hidden="true" />
          {appCopy.placeholder.badge}
        </span>
        <h2 id={`placeholder-${route}`} className={styles.heroTitle}>
          {ROUTE_TITLES[route]}
        </h2>
        <p className={styles.heroText}>{ROUTE_PURPOSE[route]}</p>
        <p className={styles.heroMeta}>{appCopy.placeholder.text}</p>
      </section>
      <div className={styles.skeletonRow} aria-hidden="true">
        <div className={styles.skeletonCard} />
        <div className={styles.skeletonCard} />
        <div className={styles.skeletonCard} />
      </div>
    </div>
  );
}
